<template>
  <div class="product-header-card" :class="statusType">
    <div class="status-tint"></div>

    <div class="product-info">
      <div class="product-image">
        <img :src="product.imageUrl" :alt="product.title">
      </div>
      <div class="product-details">
        <h1 class="product-title">{{ product.title }}</h1>
        <div class="product-meta">
          <span>{{ marketplaceLabel }}</span>
          <span v-if="product.brand"><span class="label">Brand: </span>{{ product.brand }}</span>
          <span><span class="label">ASIN: </span>{{ product.asin }}</span>
        </div>
        <div class="status-row">
          <span class="status-badge" :class="statusType">
            <svg v-if="isWon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="20 6 9 17 4 12"/>
            </svg>
            <svg v-else-if="isSuppressed" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <circle cx="12" cy="12" r="10"/>
              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07"/>
            </svg>
            <svg v-else-if="isContested" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <polyline points="17 1 21 5 17 9"/>
              <path d="M3 11V9a4 4 0 0 1 4-4h14"/>
              <polyline points="7 23 3 19 7 15"/>
              <path d="M21 13v2a4 4 0 0 1-4 4H3"/>
            </svg>
            <svg v-else viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
              <line x1="12" y1="5" x2="12" y2="19"/>
              <polyline points="19 12 12 19 5 12"/>
            </svg>
            {{ product.status.label }}
          </span>
          <!-- <span v-if="product.status.timeAgo" class="status-time">{{ statusTimeText }}</span> -->
        </div>
      </div>
    </div>

    <div class="product-stats">
      <div class="stat-item">
        <div class="stat-label">Your Price</div>
        <div class="stat-value">{{ currencySymbol }}{{ formatPrice(product.pricing.yourPrice) }}</div>
      </div>

      <div class="stat-item">
        <div class="stat-label">
          Buy Box Price
          <span class="tooltip-trigger">
            <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4M12 8h.01"></path></svg>
            <span class="tooltip-content">
              <div class="tooltip-title">Buy Box Price</div>
              <p>The landed price of the seller currently winning the Buy Box.</p>
              <p>Shows "—" when the Buy Box is suppressed.</p>
            </span>
          </span>
        </div>
        <div class="stat-value" :class="bbPriceClass">{{ bbPriceDisplay }}</div>
        <div class="stat-subtext" :class="bbPriceSubClass">{{ bbPriceSub }}</div>
      </div>

      <div class="stat-item">
        <div class="stat-label">
          Price Gap
          <span class="tooltip-trigger">
            <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4M12 8h.01"></path></svg>
            <span class="tooltip-content">
              <div class="tooltip-title">Price Gap</div>
              <p>The difference between your price and the Buy Box price. A positive gap means you're priced higher.</p>
            </span>
          </span>
        </div>
        <div class="stat-value" :class="priceGapClass">{{ priceGapDisplay }}</div>
        <div class="stat-subtext" :class="priceGapSubClass">{{ priceGapSub }}</div>
      </div>

      <div class="stat-item">
        <div class="stat-label">
          {{ lossLabel }}
          <span class="tooltip-trigger">
            <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4M12 8h.01"></path></svg>
            <span class="tooltip-content">
              <div class="tooltip-title">{{ lossLabel }}</div>
              <p>{{ lossTooltipText }}</p>
            </span>
          </span>
        </div>
        <div class="stat-value" :class="lossValueClass">{{ lossValueDisplay }}</div>
        <div class="stat-subtext" :class="lossSubClass">{{ lossSub }}</div>
      </div>

      <div class="stat-item">
        <div class="stat-label">
          FBA Stock
          <span class="tooltip-trigger">
            <svg class="info-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><circle cx="12" cy="12" r="10"></circle><path d="M12 16v-4M12 8h.01"></path></svg>
            <span class="tooltip-content">
              <div class="tooltip-title">FBA Stock</div>
              <p><strong>Fulfillable:</strong> Ships today from FC.</p>
              <p><strong>Transferring:</strong> Moving between FCs — Amazon can still route orders to these units.</p>
            </span>
          </span>
        </div>
        <div class="stat-value success">{{ product.pricing.fbaStock }}</div>
        <div class="stat-subtext">Fulfillable: {{ product.pricing.fbaStock }} · Transferring: {{ product.pricing.fbaTransferring || 0 }}</div>
      </div>
    </div>
  </div>
</template>

<script>
import { getCurrencyForMarketplace } from '../../utils/currency'

export default {
  name: 'ProductHeader',
  props: {
    product: {
      type: Object,
      required: true
    }
  },
  computed: {
    statusType: function() {
      return this.product.status.type || 'lost'
    },
    isWon: function() {
      return this.statusType === 'won' || this.statusType === 'owned'
    },
    isLost: function() {
      return this.statusType === 'lost'
    },
    isSuppressed: function() {
      return this.statusType === 'suppressed'
    },
    isContested: function() {
      return this.statusType === 'contested'
    },
    marketplaceLabel: function() {
      var m = (this.product.marketplace || 'US').toUpperCase()
      if (m === 'US') return '🇺🇸 US Marketplace'
      if (m === 'UK') return '🇬🇧 UK Marketplace'
      if (m === 'DE') return '🇩🇪 DE Marketplace'
      if (m === 'CA') return '🇨🇦 CA Marketplace'
      return m + ' Marketplace'
    },
    currencySymbol: function() {
      return getCurrencyForMarketplace(this.product.marketplace)
    },
    alertBanner: function() {
      if (this.isWon) return { visible: false }
      if (this.isLost) return {
        visible: true,
        bannerClass: 'banner-lost',
        text: 'A competitor is winning the Buy Box at a lower price. Review the diagnosis below to decide your next step.'
      }
      if (this.isSuppressed) return {
        visible: true,
        bannerClass: 'banner-suppressed',
        text: 'Amazon has suppressed the Buy Box — no seller is winning it. Likely cause: external retailers have a lower price.'
      }
      if (this.isContested) return {
        visible: true,
        bannerClass: 'banner-contested',
        text: 'The Buy Box is rotating between you and a competitor. Prices are closely matched — Amazon is sharing impressions.'
      }
      return { visible: false }
    },
    statusTimeText: function() {
      var timeAgo = this.product.status.timeAgo || ''
      if (this.isWon) return 'Holding for ' + timeAgo
      if (this.isContested) return 'Rotating · ' + timeAgo
      return 'Since ' + timeAgo
    },

    bbPriceDisplay: function() {
      if (this.isWon) return this.currencySymbol + this.formatPrice(this.product.pricing.yourPrice)
      if (this.isSuppressed) return '—'
      if (this.isContested) return this.currencySymbol + this.formatPrice(this.product.pricing.buyBoxPrice || this.product.pricing.yourPrice)
      return this.product.pricing.buyBoxPrice ? this.currencySymbol + this.formatPrice(this.product.pricing.buyBoxPrice) : '—'
    },
    bbPriceClass: function() {
      if (this.isWon) return 'success'
      if (this.isSuppressed) return 'muted'
      if (this.isContested) return 'info'
      return 'highlight'
    },
    bbPriceSub: function() {
      if (this.isWon) return "You're winning"
      if (this.isSuppressed) return 'No Buy Box active'
      if (this.isContested) return 'Prices matched'
      return this.product.pricing.buyBoxPrice ? 'Competitor winning' : 'No data'
    },
    bbPriceSubClass: function() {
      if (this.isWon) return 'ok'
      if (this.isSuppressed) return ''
      if (this.isContested) return 'info'
      return 'warn'
    },

    priceGapDisplay: function() {
      if (this.isWon) return this.currencySymbol + '0.00'
      if (this.isSuppressed) return '—'
      if (this.isContested) return '-' + this.currencySymbol + '0.00'
      return this.product.pricing.priceGap
        ? '+' + this.currencySymbol + this.formatPrice(this.product.pricing.priceGap)
        : this.currencySymbol + '0.00'
    },
    priceGapClass: function() {
      if (this.isWon) return 'success'
      if (this.isSuppressed) return 'muted'
      if (this.isContested) return 'info'
      return 'warning'
    },
    priceGapSub: function() {
      if (this.isWon) return 'No gap'
      if (this.isSuppressed) return 'Not applicable'
      if (this.isContested) return 'Prices tied'
      return this.product.pricing.priceGap
        ? this.calculatePercentage() + '% above Buy Box'
        : 'No gap'
    },
    priceGapSubClass: function() {
      if (this.isWon) return 'ok'
      if (this.isSuppressed) return ''
      if (this.isContested) return 'info'
      return 'warn'
    },

    lossLabel: function() {
      if (this.isWon) return 'Daily Revenue'
      if (this.isSuppressed) return 'Est. Daily Impact'
      if (this.isContested) return 'BB Win Rate (24h)'
      return 'Est. Daily Loss'
    },
    lossValueDisplay: function() {
      if (this.isContested) return '-60%'
      var amount = Math.abs(this.product.pricing.estDailyLoss || 0)
      var formatted = this.currencySymbol + this.formatPrice(amount)
      return this.isWon ? formatted : '-' + formatted
    },
    lossValueClass: function() {
      if (this.isWon) return 'success'
      if (this.isContested) return 'info'
      return 'critical'
    },
    lossSub: function() {
      if (this.isWon) return 'Based on BB-held days avg'
      if (this.isSuppressed) return 'Fallback: 30d avg (no BB data)'
      if (this.isContested) return 'Partial revenue capture'
      return 'Based on BB-held days avg'
    },
    lossSubClass: function() {
      if (this.isWon) return 'ok'
      if (this.isContested) return 'info'
      return 'bad'
    },
    lossTooltipText: function() {
      if (this.isWon) return 'Revenue you earn while holding the Buy Box.'
      if (this.isSuppressed) return 'Estimated daily revenue impact from Buy Box suppression.'
      if (this.isContested) return 'Percentage of time you hold the Buy Box in the last 24 hours.'
      return 'Revenue you may be losing while not holding the Buy Box.'
    }
  },
  methods: {
    formatPrice: function(val) {
      if (val == null || val === '') return '0.00'
      return Number(val).toFixed(2)
    },
    calculatePercentage: function() {
      if (this.product.pricing.yourPrice && this.product.pricing.priceGap) {
        return Math.round((this.product.pricing.priceGap / this.product.pricing.yourPrice) * 100)
      }
      return 0
    }
  }
}
</script>



// WEBPACK FOOTER //
// ProductHeader.vue?f3f7b0ec